import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Star, Plus, Edit2, Trash2, Eye, EyeOff } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getAdminEmail = () => localStorage.getItem('adminEmail');

const StarInput = ({ rating, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        onClick={() => onChange(star)}
        className="focus:outline-none"
        data-testid={`star-input-${star}`}
      >
        <Star className={`w-6 h-6 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 hover:text-yellow-300'}`} />
      </button>
    ))}
  </div>
);

const AdminReviews = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [formData, setFormData] = useState({
    author_name: '',
    company: '',
    role: '',
    rating: 5,
    text: '',
    is_active: true,
    sort_order: 0
  });

  useEffect(() => {
    if (!getAdminEmail()) {
      navigate('/admin/login');
      return;
    }
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/admin/reviews`);
      setReviews(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load reviews', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ author_name: '', company: '', role: '', rating: 5, text: '', is_active: true, sort_order: 0 });
    setEditingReview(null);
    setShowForm(false);
  };

  const handleEdit = (review) => {
    setFormData({
      author_name: review.author_name,
      company: review.company || '',
      role: review.role || '',
      rating: review.rating,
      text: review.text,
      is_active: review.is_active,
      sort_order: review.sort_order || 0
    });
    setEditingReview(review);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.author_name || !formData.text) {
      toast({ title: 'Error', description: 'Name and review text are required', variant: 'destructive' });
      return;
    }

    try {
      if (editingReview) {
        await axios.put(`${API}/admin/reviews/${editingReview.id}`, formData);
        toast({ title: 'Success', description: 'Review updated' });
      } else {
        await axios.post(`${API}/admin/reviews`, formData);
        toast({ title: 'Success', description: 'Review created' });
      }
      resetForm();
      fetchReviews();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save review', variant: 'destructive' });
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`);
      toast({ title: 'Success', description: 'Review deleted' });
      fetchReviews();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete review', variant: 'destructive' });
    }
  };

  const toggleActive = async (review) => {
    try {
      await axios.put(`${API}/admin/reviews/${review.id}`, { is_active: !review.is_active });
      fetchReviews();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update review', variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#1e3a5f] text-white py-6">
        <div className="container mx-auto px-4 sm:px-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Manage Google Reviews</h1>
            <p className="text-blue-200 text-sm mt-1">{reviews.length} reviews ({reviews.filter(r => r.is_active).length} active)</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/admin')} variant="outline" className="text-white border-white hover:bg-white/10">
              Back to Admin
            </Button>
            <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-red-600 hover:bg-red-700" data-testid="add-review-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Review
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        {/* Form */}
        {showForm && (
          <Card className="p-6 mb-6 border-2 border-blue-200" data-testid="review-form">
            <h2 className="text-lg font-bold mb-4">{editingReview ? 'Edit Review' : 'Add New Review'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label>Reviewer Name *</Label>
                  <Input
                    value={formData.author_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, author_name: e.target.value }))}
                    placeholder="John Smith"
                    data-testid="review-author-input"
                  />
                </div>
                <div>
                  <Label>Company</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="Smith Transport"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Input
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                    placeholder="Carrier / Shipper"
                  />
                </div>
              </div>

              <div>
                <Label>Rating</Label>
                <StarInput rating={formData.rating} onChange={(r) => setFormData(prev => ({ ...prev, rating: r }))} />
              </div>

              <div>
                <Label>Review Text *</Label>
                <Textarea
                  value={formData.text}
                  onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
                  rows={3}
                  placeholder="Write the review..."
                  data-testid="review-text-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sort Order (lower = first)</Label>
                  <Input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Show on website</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#2a4d7a]" data-testid="save-review-btn">
                  {editingReview ? 'Update Review' : 'Add Review'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </Card>
        )}

        {/* Reviews List */}
        <div className="space-y-3">
          {reviews.length === 0 ? (
            <Card className="p-12 text-center">
              <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No reviews yet</p>
              <p className="text-sm text-gray-400 mt-1">Add your first Google review to display on the homepage</p>
            </Card>
          ) : (
            reviews.map((review) => (
              <Card key={review.id} className={`p-4 ${!review.is_active ? 'opacity-50 bg-gray-100' : ''}`} data-testid={`review-card-${review.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-bold text-gray-800">{review.author_name}</span>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`w-3.5 h-3.5 ${s <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                        ))}
                      </div>
                      {!review.is_active && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Hidden</span>}
                    </div>
                    <p className="text-sm text-gray-500 mb-2">
                      {[review.role, review.company].filter(Boolean).join(' — ')}
                    </p>
                    <p className="text-gray-700 text-sm italic">"{review.text}"</p>
                  </div>
                  
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(review)} title={review.is_active ? 'Hide' : 'Show'}>
                      {review.is_active ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(review)} data-testid={`edit-review-${review.id}`}>
                      <Edit2 className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(review.id)} data-testid={`delete-review-${review.id}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminReviews;
